import { Button, Card, EmptyState, Skeleton, Table, type Column } from "@helmr/ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { isApiError } from "../../api/client";
import { FailureBody } from "../home/states";
import { billing as t } from "./strings";
import s from "./billing.module.css";

export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className={s.skel} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={34} />)}
    </div>
  );
}

interface PagedLike<T> {
  rows: T[]; isPending: boolean; error: unknown; refetch: () => unknown;
  hasNextPage: boolean; fetchNextPage: () => unknown; isFetchingNextPage: boolean;
}

/** One list, four states: skeleton, error (404 / 403 / Retry with request id), empty, rows with Load more. */
export function PagedTable<T>({ query, columns, rowKey, onRowClick, label, empty, what }: {
  query: PagedLike<T>; columns: ReadonlyArray<Column<T>>; rowKey: (r: T) => string; onRowClick?: (r: T) => void;
  label: string; empty: readonly [string, string]; what: string;
}) {
  if (query.isPending) return <ListSkeleton />;
  if (query.error) return <FailureBody error={query.error} title={`Could not load ${what}`} what={what} onRetry={() => void query.refetch()} />;
  if (query.rows.length === 0) return <EmptyState compact icon="receipt" title={empty[0]} description={empty[1]} />;
  return (
    <Table label={label} columns={columns} rows={query.rows} rowKey={rowKey} onRowClick={onRowClick} minWidth={560}
      hasMore={query.hasNextPage} onLoadMore={() => void query.fetchNextPage()} loadingMore={query.isFetchingNextPage} summary={`${query.rows.length}${query.hasNextPage ? "+" : ""}`} />
  );
}

export function PageHead({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className={s.head}>
      <div><h1 tabIndex={-1}>{title}</h1>{subtitle ? <p>{subtitle}</p> : null}</div>
      {action}
    </div>
  );
}

/** `missing` set = a 404 means this record does not exist (or belongs to another gym), not "API not connected". */
export function QueryFailure({ error, what, onRetry, missing }: { error: unknown; what: string; onRetry: () => void; missing?: string }) {
  if (missing && isApiError(error) && error.status === 404) {
    return <Card><EmptyState icon="alert" title={missing} description="It may have been removed, or it belongs to another gym." action={<Button as={Link} to="/billing">{t.bill.back}</Button>} /></Card>;
  }
  return <Card><FailureBody error={error} title={`Could not load ${what}`} what={what} onRetry={onRetry} /></Card>;
}

export { Button, t };
